/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import { CommonModule } from "@angular/common";
import {
	ChangeDetectorRef,
	Component,
	NgZone,
	type OnDestroy,
	inject,
} from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import type { Subscription } from "rxjs";
import { IconComponent } from "../../icon/icon.component";
import {
	ChoiceManagerService,
	type ChoiceMessage,
} from "../choice-manager.service";
import type { CaiAction } from "./action";

@Component({
	selector: "cb-choice-widget",
	templateUrl: "./choice-widget.component.html",
	styleUrls: ["./choice-widget.component.scss"],
	imports: [CommonModule, TranslateModule, IconComponent],
	// changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChoiceWidgetComponent implements OnDestroy {
	private readonly choiceSubscription: Subscription;

	private _choices: CaiAction[] = [];
	public show = false;

	public animate = false;
	public isMultiple = false;
	public selectedValues: any[] = [];

	private _resolve: (value: any) => void = () => {};
	private readonly choicesService: ChoiceManagerService =
		inject(ChoiceManagerService);
	private readonly ngZone = inject(NgZone);
	private readonly changeDetectorRef = inject(ChangeDetectorRef);

	constructor() {
		this.choiceSubscription = this.choicesService.showSubject.subscribe(
			(message: ChoiceMessage) => {
				this.ngZone.run(() => {
					this.choices = message.choices;
					this._resolve = message.resolve;
					this.show = true;
					this.animate = message.animate || false;
					this.isMultiple = message.isMultiple || false;
					this.selectedValues = message.selectedValues || [];
					// Stesso schema delle dialog: CD subito così il template (@if show) monta l’overlay al primo click.
					this.changeDetectorRef.detectChanges();
				});
			},
		);
	}

	public get choices(): CaiAction[] {
		return this._choices;
	}

	public set choices(choices: CaiAction[]) {
		// Save the choices in reversed order
		this._choices = structuredClone(choices).reverse();
	}

	public choicesTrackBy(index: number, choice: CaiAction): unknown {
		return choice.value;
	}

	public isSelected(choice: any): boolean {
		return (
			this.isMultiple &&
			this.selectedValues.length > 0 &&
			this.selectedValues.includes(choice.value)
		);
	}

	public selectChoice(value: any): void {
		this.show = false;
		this._resolve(value);
	}

	ngOnDestroy(): void {
		this.choiceSubscription.unsubscribe();
	}
}
